// src/components/ChannelChat.js

import React, { useState, useEffect, useRef } from 'react'
import {
  collection,
  onSnapshot,
  addDoc,
  query,
  orderBy,
  serverTimestamp,
  where,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  setDoc
} from 'firebase/firestore'
import {
  ref,
  uploadBytesResumable,
  getDownloadURL
} from 'firebase/storage'
import { db, storage } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import {
  TextField,
  Button,
  IconButton,
  Stack,
  Snackbar,
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent
} from '@mui/material'
import PhotoCamera from '@mui/icons-material/PhotoCamera'

import ChatWindow from './ChatWindow'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function ChannelChat({ channel, userRole }) {
  const { t } = useTranslation() // i18n Hook

  const { currentUser } = useAuth()
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(null)
  const [previewURL, setPreviewURL] = useState(null)

  const [replyTo, setReplyTo] = useState(null)
  const uploadTaskRef = useRef(null)

  // Snackbar state (opsiyonel)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  // lastRead'ı güncelleme fonksiyonu
  const updateLastRead = async (channelId) => {
    if (!currentUser) return
    const statusRef = doc(db, 'userChatStatus', `${currentUser.uid}_${channelId}`)
    await setDoc(statusRef, { lastRead: serverTimestamp() }, { merge: true })
  }

  // Kanal mesajlarını dinle
  useEffect(() => {
    if (!channel) return

    const msgRef = collection(db, 'channelMessages')
    const q = query(msgRef, where('channelId', '==', channel.id), orderBy('createdAt', 'asc'))

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }))
      setMessages(msgs)
      await updateLastRead(channel.id)
    })

    return () => unsubscribe()
  }, [channel, currentUser])

  // Mesaj düzenleme
  const handleEditMessage = async (messageId, newText) => {
    try {
      const messageRef = doc(db, 'channelMessages', messageId)
      await updateDoc(messageRef, {
        text: newText,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error(`${t('channelChat.sendError')}`, error)
    }
  }

  // Mesaj silme
  const handleDeleteMessage = async (messageId) => {
    const confirmDelete = window.confirm(t('channelChat.deleteConfirm'))
    if (!confirmDelete) return

    try {
      const messageRef = doc(db, 'channelMessages', messageId)
      await deleteDoc(messageRef)
    } catch (error) {
      console.error(`${t('channelChat.deleteError')}`, error)
    }
  }

  // Dosya seçildi
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setSelectedFile(file)
      setUploadProgress(null)
      uploadTaskRef.current = null

      // Önizleme
      const tempUrl = URL.createObjectURL(file)
      setPreviewURL(tempUrl)
    }
  }

  // Mesaja yanıt verme
  const handleReplyMessage = (message) => {
    setReplyTo(message)
  }
  const handleCancelReply = () => {
    setReplyTo(null)
  }

  // Mesaj gönder
  const handleSendChannelMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() && !selectedFile) return

    // Observer rolü kontrolü
    if (userRole === 'observer') {
      alert(t('channelChat.observerSendError'))
      return
    }

    try {
      setUploadProgress(0)
      let fileUrl = null
      let fileType = null

      if (selectedFile) {
        fileType = selectedFile.type
        const fileRef = ref(storage, `channelUploads/${Date.now()}_${selectedFile.name}`)
        const uploadTask = uploadBytesResumable(fileRef, selectedFile)
        uploadTaskRef.current = uploadTask

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            setUploadProgress(progress)
          },
          (error) => {
            console.error('Dosya yükleme hatası:', error)
            setUploadProgress(null)
            uploadTaskRef.current = null
          },
          async () => {
            fileUrl = await getDownloadURL(fileRef)
            setUploadProgress(null)
            uploadTaskRef.current = null

            await addDoc(collection(db, 'channelMessages'), {
              channelId: channel.id,
              text: newMessage,
              fileUrl,
              fileType,
              userEmail: currentUser.email,
              userNickname: currentUser.nickname || '',
              avatarUrl: currentUser.avatarUrl || '',
              createdAt: serverTimestamp(),
              userId: currentUser.uid,
              likedBy: [],
              replyToId: replyTo ? replyTo.id : null,
              replyToText: replyTo ? replyTo.text : null,
              replyToFileUrl: replyTo ? replyTo.fileUrl : null,
              replyToFileType: replyTo ? replyTo.fileType : null
            })

            setNewMessage('')
            setSelectedFile(null)
            setPreviewURL(null)
            setReplyTo(null)
          }
        )
      } else {
        // Sadece metin
        await addDoc(collection(db, 'channelMessages'), {
          channelId: channel.id,
          text: newMessage,
          fileUrl,
          fileType,
          userEmail: currentUser.email,
          userNickname: currentUser.nickname || '',
          avatarUrl: currentUser.avatarUrl || '',
          createdAt: serverTimestamp(),
          userId: currentUser.uid,
          likedBy: [],
          replyToId: replyTo ? replyTo.id : null,
          replyToText: replyTo ? replyTo.text : null,
          replyToFileUrl: replyTo ? replyTo.fileUrl : null,
          replyToFileType: replyTo ? replyTo.fileType : null
        })

        setNewMessage('')
        setSelectedFile(null)
        setUploadProgress(null)
        setPreviewURL(null)
        setReplyTo(null)
      }
    } catch (err) {
      console.error(`${t('channelChat.sendError')}`, err)
      setUploadProgress(null)
    }
  }

  // Like butonuna tıklama
  const handleToggleLikeMessage = async (messageId) => {
    if (!currentUser) return

    try {
      const messageRef = doc(db, 'channelMessages', messageId)
      const snapshot = await getDoc(messageRef)
      if (!snapshot.exists()) return

      const messageData = snapshot.data()
      const currentLikes = messageData.likedBy || []
      const hasLiked = currentLikes.includes(currentUser.uid)

      let updatedLikes = []
      if (hasLiked) {
        updatedLikes = currentLikes.filter((uid) => uid !== currentUser.uid)
      } else {
        updatedLikes = [...currentLikes, currentUser.uid]
      }

      await updateDoc(messageRef, { likedBy: updatedLikes })
    } catch (error) {
      console.error('Like işlemi sırasında hata oluştu:', error)
    }
  }

  // Yükleme iptali
  const handleCancelUpload = () => {
    if (uploadTaskRef.current) {
      uploadTaskRef.current.cancel()
      uploadTaskRef.current = null
    }
    setSelectedFile(null)
    setPreviewURL(null)
    setUploadProgress(null)
    setNewMessage('')
  }

  // Snackbar kapatma
  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  return (
    <Box>
      {/* Mesajları göster */}
      <ChatWindow
        messages={messages}
        currentUser={currentUser}
        onEditMessage={handleEditMessage}
        onDeleteMessage={handleDeleteMessage}
        onToggleLike={handleToggleLikeMessage}
        onReplyMessage={handleReplyMessage}
        userRole={userRole}
      />

      {/* Eğer bir mesaja cevap veriyorsak gösterilecek uyarı kartı */}
      {replyTo && (
        <Card sx={{ mt: 2, backgroundColor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('channelChat.replyingMessage')}
            </Typography>

            {replyTo.text && (
              <Typography variant="subtitle2" sx={{ fontStyle: 'italic' }}>
                {replyTo.text}
              </Typography>
            )}

            {replyTo.fileUrl && replyTo.fileType?.startsWith('image/') && (
              <Box sx={{ mt: 1 }}>
                <img
                  src={replyTo.fileUrl}
                  alt="reply-img-preview"
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '4px'
                  }}
                />
              </Box>
            )}
            {replyTo.fileUrl && replyTo.fileType?.startsWith('video/') && (
              <Box sx={{ mt: 1 }}>
                <video
                  src={replyTo.fileUrl}
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '4px'
                  }}
                  muted
                  autoPlay
                  loop
                />
              </Box>
            )}

            <Button
              variant="text"
              color="error"
              onClick={handleCancelReply}
              sx={{ mt: 1 }}
            >
              {t('channelChat.replyCancel')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Mesaj Gönderme Formu (Observer değilse) */}
      {userRole !== 'observer' && (
        <form onSubmit={handleSendChannelMessage}>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <TextField
              label={t('channelChat.placeholder')}
              variant="outlined"
              fullWidth
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <input
              accept="image/*,video/*,audio/*"
              style={{ display: 'none' }}
              id="icon-button-file-channel"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="icon-button-file-channel">
              <IconButton color="primary" aria-label="upload file" component="span">
                <PhotoCamera />
              </IconButton>
            </label>
            <Button type="submit" variant="contained">
              {t('channelChat.sendButton')}
            </Button>
          </Stack>
        </form>
      )}

      {/* Dosya seçiliyse göster */}
      {selectedFile && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('channelChat.selectedFile')} {selectedFile.name}
            <Button variant="text" onClick={handleCancelUpload} sx={{ ml: 1 }}>
              {t('channelChat.cancelFile')}
            </Button>
          </Typography>
        </Box>
      )}

      {/* Önizleme */}
      {previewURL && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">
            {t('channelChat.previewTitle')}
          </Typography>
          {selectedFile?.type.startsWith('image/') && (
            <img
              src={previewURL}
              alt="preview-img"
              style={{
                maxWidth: '100%',
                borderRadius: 8,
                marginTop: 8
              }}
            />
          )}
          {selectedFile?.type.startsWith('video/') && (
            <video
              src={previewURL}
              controls
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                borderRadius: 8,
                marginTop: 8,
                objectFit: 'contain'
              }}
            />
          )}
          {selectedFile?.type.startsWith('audio/') && (
            <audio
              src={previewURL}
              controls
              style={{ width: '100%', marginTop: 8 }}
            />
          )}
        </Box>
      )}

      {/* Yüklenme ilerlemesi */}
      {uploadProgress !== null && uploadProgress < 100 && uploadProgress > 0 && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" align="center" sx={{ mt: 1 }}>
            {t('channelChat.uploadingProgress', {
              progress: Math.round(uploadProgress)
            })}
          </Typography>

          <Button
            variant="outlined"
            color="error"
            onClick={handleCancelUpload}
            sx={{ mt: 1, display: 'block', mx: 'auto' }}
          >
            {t('channelChat.cancelUpload')}
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={
          <Button color="secondary" size="small" onClick={() => setSnackbarOpen(false)}>
            Kapat
          </Button>
        }
      />
    </Box>
  )
}